/**
 * FFActionBar — Bandeaux contextuels + barre d'actions fixe selon le statut.
 *
 * Affichage adaptatif :
 *   - statut "a_valider" : barre fixe Valider/Rejeter
 *   - statut "a_payer"   : bandeau régénérer + bandeau dévalider + barre fixe "Marquer payée"
 *   - statut "a_exporter": bandeau "retirer du lot d'export"
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, XCircle, CreditCard, RefreshCw, ArrowLeft, Loader2, Trash2 } from "lucide-react";

const MODES_PAIEMENT = [
  { value: "virement", label: "Virement bancaire" },
  { value: "especes", label: "Espèces (caisse)" },
  { value: "ayapay", label: "AyaPay (CB ou TWINT)" },
  { value: "autre", label: "Autre" },
];

export default function FFActionBar({
  f, onValider, onRejeter, onRegenerer, onDevalider, onRevertExport, onPayer,
  onAnnulerDefinitivement, isAdmin = false,
}) {
  // États dialogs locaux
  const [showDevaliderDialog, setShowDevaliderDialog] = useState(false);
  const [devaliderMotif, setDevaliderMotif] = useState("");
  const [devaliderLoading, setDevaliderLoading] = useState(false);

  const [showRevertExportDialog, setShowRevertExportDialog] = useState(false);
  const [revertExportMotif, setRevertExportMotif] = useState("Retiré du lot d'export");
  const [revertExportLoading, setRevertExportLoading] = useState(false);

  const [showPayerDialog, setShowPayerDialog] = useState(false);
  // iter342 — Annulation complète (admin only, irréversible)
  const [showAnnulDialog, setShowAnnulDialog] = useState(false);
  const [annulMotif, setAnnulMotif] = useState("");
  const [annulConfirm, setAnnulConfirm] = useState("");
  const [annulLoading, setAnnulLoading] = useState(false);
  const [paiement, setPaiement] = useState({
    date: new Date().toISOString().slice(0, 10), montant: 0, mode: "virement", note: "",
  });

  const submitDevalider = async () => {
    setDevaliderLoading(true);
    try { await onDevalider(devaliderMotif); setShowDevaliderDialog(false); }
    catch { /* déjà toasté */ }
    finally { setDevaliderLoading(false); }
  };
  const submitRevertExport = async () => {
    setRevertExportLoading(true);
    try { await onRevertExport(revertExportMotif); setShowRevertExportDialog(false); }
    catch { /* déjà toasté */ }
    finally { setRevertExportLoading(false); }
  };
  const submitPayer = async () => {
    try { await onPayer(paiement); setShowPayerDialog(false); }
    catch { /* déjà toasté */ }
  };
  const submitAnnulation = async () => {
    setAnnulLoading(true);
    try { await onAnnulerDefinitivement(annulMotif); setShowAnnulDialog(false); }
    catch { /* déjà toasté */ }
    finally { setAnnulLoading(false); }
  };

  const refFacture = f.reference_interne || f.numero_facture || "";
  const annulPossible = isAdmin && f.statut !== "payee" && !f.ecriture_paiement_id && !f.paye_at;

  return (
    <>
      {/* ===== Statut "a_exporter" : bouton retrait du lot ===== */}
      {f.statut === "a_exporter" && (
        <Card className="p-3 mt-4 bg-blue-50 border-blue-200" data-testid="ff-revert-export-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs text-blue-800 flex-1">
              📤 <strong>Facture préparée pour l&apos;export</strong> (lot pain.001). Vous pouvez encore la retirer du lot avant génération du fichier bancaire.
            </div>
            <Button
              size="sm" variant="outline"
              onClick={() => setShowRevertExportDialog(true)}
              className="text-xs border-blue-400 text-blue-700 hover:bg-blue-100"
              data-testid="ff-revert-export-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Retirer du lot d&apos;export
            </Button>
          </div>
        </Card>
      )}

      {/* ===== Statut "a_payer" : régénérer + dévalider + bouton "marquer payée" ===== */}
      {f.statut === "a_payer" && (
        <>
          <Card className="p-3 mt-4 bg-blue-50 border-blue-200 text-xs text-blue-800" data-testid="ff-regenerer-hint">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span>
                💡 <strong>TVA manquante ou montant à corriger ?</strong> Modifiez les champs ci-dessus puis régénérez les écritures comptables.
              </span>
              <Button
                size="sm" variant="outline" onClick={onRegenerer}
                className="text-xs border-blue-400 text-blue-700 hover:bg-blue-100"
                data-testid="ff-regenerer-btn"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Régénérer les écritures
              </Button>
            </div>
          </Card>
          <Card className="p-3 mt-2 bg-amber-50 border-amber-200 text-xs text-amber-800" data-testid="ff-devalider-hint">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span>
                ⏪ <strong>Repasser cette facture en « À valider » ?</strong> Les écritures comptables seront supprimées et la facture pourra être modifiée librement, puis re-validée.
              </span>
              <Button
                size="sm" variant="outline"
                onClick={() => setShowDevaliderDialog(true)}
                className="text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                data-testid="ff-devalider-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Dévalider
              </Button>
            </div>
          </Card>
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 flex gap-2 z-40 lg:left-64">
            <Button
              variant="outline"
              onClick={() => { setShowPayerDialog(true); setPaiement((p) => ({ ...p, montant: f.reste_a_payer ?? f.montant_ttc ?? 0 })); }}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white border-0"
              data-testid="ff-marquer-paye-btn"
            >
              <CreditCard className="w-4 h-4 mr-1.5" /> Marquer comme payée
            </Button>
          </div>
        </>
      )}

      {/* ===== Statut "a_valider" : Valider / Rejeter ===== */}
      {f.statut === "a_valider" && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 flex gap-2 z-40 lg:left-64">
          <Button
            variant="outline" onClick={onRejeter}
            className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
            data-testid="ff-rejeter-btn"
          >
            <XCircle className="w-4 h-4 mr-1.5" /> Rejeter
          </Button>
          <Button
            onClick={onValider}
            className="flex-[2] bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="ff-valider-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Valider et comptabiliser
          </Button>
        </div>
      )}

      {/* ===== iter342 — Annulation complète (admin) ===== */}
      {annulPossible && (
        <Card className="p-3 mt-2 bg-red-50 border-red-200" data-testid="ff-annulation-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs text-red-800 flex-1">
              🗑️ <strong>Annuler complètement cette facture</strong>
              {f.statut === "exportee" && " (même exportée dans un lot pain.001)"} : les écritures
              comptables, les notes de crédit, les fichiers et la facture elle-même sont supprimés
              définitivement. Elle est aussi retirée du lot d&apos;export, sans toucher aux autres factures du lot.
            </div>
            <Button
              size="sm" variant="outline"
              onClick={() => { setAnnulMotif(""); setAnnulConfirm(""); setShowAnnulDialog(true); }}
              className="text-xs border-red-400 text-red-700 hover:bg-red-100"
              data-testid="ff-annuler-definitivement-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Annuler complètement
            </Button>
          </div>
        </Card>
      )}

      {/* ===== Dialog : retrait du lot d'export ===== */}
      <Dialog open={showRevertExportDialog} onOpenChange={setShowRevertExportDialog}>
        <DialogContent data-testid="ff-revert-export-dialog">
          <DialogHeader>
            <DialogTitle>Retirer du lot d&apos;export ?</DialogTitle>
            <DialogDescription>
              La facture repassera en statut « À payer ». Elle pourra être réintégrée à un futur lot pain.001.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="revert-export-motif" className="text-sm">Motif (optionnel)</Label>
            <Input
              id="revert-export-motif" value={revertExportMotif}
              onChange={(e) => setRevertExportMotif(e.target.value)}
              placeholder="Motif du retrait"
              data-testid="ff-revert-export-motif-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRevertExportDialog(false)} disabled={revertExportLoading} data-testid="ff-revert-export-cancel-btn">
              Annuler
            </Button>
            <Button
              onClick={submitRevertExport} disabled={revertExportLoading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="ff-revert-export-confirm-btn"
            >
              {revertExportLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Retrait…</>
              ) : (
                <><ArrowLeft className="w-4 h-4 mr-2" /> Confirmer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Dialog : dévalidation ===== */}
      <Dialog open={showDevaliderDialog} onOpenChange={setShowDevaliderDialog}>
        <DialogContent data-testid="ff-devalider-dialog">
          <DialogHeader>
            <DialogTitle>Dévalider cette facture ?</DialogTitle>
            <DialogDescription>
              La facture repassera en statut « À valider ». Toutes les écritures comptables liées seront supprimées (la TVA sera retirée).
              Vous pourrez ensuite modifier librement et re-valider quand c&apos;est correct.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="devalider-motif" className="text-sm">Motif (optionnel)</Label>
            <Input
              id="devalider-motif" value={devaliderMotif}
              onChange={(e) => setDevaliderMotif(e.target.value)}
              placeholder="Ex: correction compte de charge"
              data-testid="ff-devalider-motif-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDevaliderDialog(false)} disabled={devaliderLoading} data-testid="ff-devalider-cancel-btn">
              Annuler
            </Button>
            <Button
              onClick={submitDevalider} disabled={devaliderLoading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="ff-devalider-confirm-btn"
            >
              {devaliderLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Dévalidation…</>
              ) : (
                <><ArrowLeft className="w-4 h-4 mr-2" /> Confirmer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== iter342 — Dialog : annulation complète ===== */}
      <Dialog open={showAnnulDialog} onOpenChange={setShowAnnulDialog}>
        <DialogContent data-testid="ff-annulation-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Annuler définitivement {refFacture} ?</DialogTitle>
            <DialogDescription>
              Cette action est <strong>irréversible</strong>. Seront supprimés : toutes les écritures
              comptables de cette facture (charge, TVA, notes de crédit), les fichiers joints et la
              facture elle-même. Le lien avec le lot d&apos;export bancaire est retiré ; les autres
              factures du lot ne bougent pas. Une trace complète reste dans le journal d&apos;audit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {f.statut === "exportee" && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
                ⚠️ Le fichier de paiement pain.001 a peut-être déjà été transmis à la banque : il
                contiendra toujours ce paiement. Vérifiez auprès de la banque qu&apos;il ne sera pas exécuté.
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="annul-motif" className="text-sm">Motif de l&apos;annulation (obligatoire)</Label>
              <Input
                id="annul-motif" value={annulMotif}
                onChange={(e) => setAnnulMotif(e.target.value)}
                placeholder="Ex: facture en double / fournisseur erroné"
                data-testid="ff-annulation-motif-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="annul-confirm" className="text-sm">
                Tapez <code className="font-mono bg-gray-100 px-1 rounded">{refFacture}</code> pour confirmer
              </Label>
              <Input
                id="annul-confirm" value={annulConfirm}
                onChange={(e) => setAnnulConfirm(e.target.value)}
                placeholder={refFacture}
                className="font-mono"
                data-testid="ff-annulation-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAnnulDialog(false)} disabled={annulLoading} data-testid="ff-annulation-cancel-btn">
              Renoncer
            </Button>
            <Button
              onClick={submitAnnulation}
              disabled={annulLoading || annulMotif.trim().length < 3 || annulConfirm.trim() !== refFacture}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="ff-annulation-confirm-btn"
            >
              {annulLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Annulation…</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Dialog : enregistrement paiement ===== */}
      <Dialog open={showPayerDialog} onOpenChange={setShowPayerDialog}>
        <DialogContent data-testid="ff-payer-dialog">
          <DialogHeader>
            <DialogTitle>Enregistrer le paiement</DialogTitle>
            <DialogDescription>
              Marquer la facture comme payée. Une écriture comptable de paiement sera créée automatiquement.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={paiement.date} onChange={(e) => setPaiement({ ...paiement, date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Montant</Label>
              <Input
                type="number" step="0.01"
                value={paiement.montant}
                onChange={(e) => setPaiement({ ...paiement, montant: e.target.value })}
                className="font-mono text-right"
                data-testid="ff-payer-montant"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Mode</Label>
              <Select value={paiement.mode} onValueChange={(v) => setPaiement({ ...paiement, mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODES_PAIEMENT.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Note</Label>
              <Input
                value={paiement.note}
                onChange={(e) => setPaiement({ ...paiement, note: e.target.value })}
                placeholder="Optionnel"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayerDialog(false)}>Annuler</Button>
            <Button
              onClick={submitPayer}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ff-payer-submit-btn"
            >
              Confirmer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
